import { z } from 'zod';

// Offres et abonnement des cabinets (BA-084 à BA-087, ADR-029). Montants hors taxes en
// millimes ; une limite nulle signifie « illimité ».

/** Fonctions réservées à certaines offres. */
export const PLAN_FEATURES = [
  'public_api',
  'webhooks',
  'direct_debit',
  'sso',
  'sftp',
  'llm_assist',
] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

/** Limites d'une offre : utilisateurs actifs ou invités, contrats en cours, assureurs. */
export const PLAN_LIMITS = ['users', 'activeContracts', 'insurers'] as const;
export type PlanLimit = (typeof PLAN_LIMITS)[number];

export const SUBSCRIPTION_STATUSES = [
  'trial',
  'active',
  'past_due',
  'suspended',
  'cancelled',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const BILLING_CYCLES = ['monthly', 'annual'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

/** Essai gratuit d'un nouveau cabinet, sur l'offre Pro, en mode brouillon. */
export const TRIAL_DAYS = 30;
export const TRIAL_PLAN_CODE = 'PRO';

const millimes = z.number().int().min(0);
const limitValue = z.number().int().min(1).nullable();

export const planLimitsSchema = z.object({
  users: limitValue,
  activeContracts: limitValue,
  insurers: limitValue,
});
export type PlanLimits = z.infer<typeof planLimitsSchema>;

export const planTariffSchema = z.object({
  whatsapp: millimes,
  sms: millimes,
  email: millimes,
});

export const planSchema = z.object({
  id: z.string(),
  code: z.string(),
  version: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  /** Offre propre au cabinet (négociée) ; sinon offre du catalogue. */
  custom: z.boolean(),
  monthlyPrice: millimes,
  annualPrice: millimes,
  /** Messages réels inclus chaque mois, tous canaux confondus. */
  includedMessages: millimes,
  /** Prix par message au-delà des messages inclus. */
  messageTariff: planTariffSchema,
  /** Commission par paiement en ligne confirmé. */
  paymentFee: millimes,
  limits: planLimitsSchema,
  features: z.array(z.enum(PLAN_FEATURES)),
  status: z.enum(['active', 'archived']),
});
export type Plan = z.infer<typeof planSchema>;
export const planListSchema = z.object({ items: z.array(planSchema) });

/** Création d'une offre ou d'une nouvelle version d'une offre existante (même code). */
export const planInputSchema = z.object({
  code: z.string().regex(/^[A-Z0-9_-]{1,20}$/, 'Code : 1 à 20 caractères A-Z, 0-9, _ ou -'),
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(300).nullable().optional(),
  /** Offre réservée à un cabinet (tarif négocié). */
  tenantId: z.string().nullable().optional(),
  monthlyPrice: millimes,
  annualPrice: millimes,
  includedMessages: millimes,
  messageTariff: planTariffSchema,
  paymentFee: millimes,
  limits: planLimitsSchema,
  features: z.array(z.enum(PLAN_FEATURES)),
});

export const subscriptionSchema = z.object({
  status: z.enum(SUBSCRIPTION_STATUSES),
  billingCycle: z.enum(BILLING_CYCLES),
  /** Cycle appliqué au prochain renouvellement, s'il change. */
  nextBillingCycle: z.enum(BILLING_CYCLES).nullable(),
  trialEndsAt: z.string().nullable(),
  /** Offre choisie pendant l'essai : l'abonnement démarre à la fin de l'essai. */
  confirmedAt: z.string().nullable(),
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  plan: planSchema,
});

export const subscriptionUsageSchema = z.object({
  /** Messages réels envoyés depuis le début du mois. */
  messagesThisMonth: z.number().int(),
  users: z.number().int(),
  activeContracts: z.number().int(),
  insurers: z.number().int(),
});

export const subscriptionViewSchema = z.object({
  /** `null` : cabinet historique sans abonnement (aucune limite). */
  subscription: subscriptionSchema.nullable(),
  usage: subscriptionUsageSchema,
  events: z.array(
    z.object({
      kind: z.string(),
      at: z.string(),
      detail: z.record(z.string(), z.unknown()),
    }),
  ),
});

export const subscriptionChangeSchema = z
  .object({
    planId: z.string().min(1),
    billingCycle: z.enum(BILLING_CYCLES),
  })
  .strict();

export const subscriptionQuoteSchema = z.object({
  /** Montant hors taxes ajouté (positif) ou crédité (négatif) au prochain relevé. */
  adjustment: z.number().int(),
  /** Date d'effet du changement. */
  effectiveOn: z.string(),
  /** Usages au-delà des limites de l'offre visée : changement impossible tant qu'ils restent. */
  blockers: z.array(
    z.object({ limit: z.enum(PLAN_LIMITS), current: z.number().int(), max: z.number().int() }),
  ),
});

// --- Encaissement (BA-089) et inscription (BA-088) ---

export const subscriptionInvoiceSchema = z.object({
  id: z.string(),
  number: z.string(),
  period: z.string(),
  subtotal: z.number().int(),
  vat: z.number().int(),
  total: z.number().int(),
  issuedOn: z.string(),
  dueOn: z.string(),
  status: z.enum(['open', 'paid', 'void']),
  paidAt: z.string().nullable(),
  method: z.enum(['direct_debit', 'transfer', 'check', 'other']).nullable(),
  /** Dernier échec de prélèvement. */
  failureReason: z.string().nullable(),
  /** Relevé PDF, téléchargeable par `/v1/exports-files/{id}`. */
  fileId: z.string().nullable(),
});
export const subscriptionInvoiceListSchema = z.object({
  items: z.array(subscriptionInvoiceSchema),
  paymentMethod: z.enum(['transfer', 'direct_debit']),
  mandateStatus: z.enum(['none', 'pending', 'active', 'cancelled']),
});

/** Demande d'inscription d'une agence depuis la page publique (BA-088). */
export const signupInputSchema = z
  .object({
    agencyName: z.string().trim().min(2).max(120),
    agentCode: z.string().trim().max(40).optional(),
    taxId: z
      .string()
      .trim()
      .regex(/^[0-9]{7}[A-Za-z]([/ ]?[A-Za-z]){0,3}([/ ]?[0-9]{3})?$/, 'Matricule fiscal invalide'),
    city: z.string().trim().min(2).max(80),
    insurers: z.string().trim().min(2).max(300),
    adminName: z.string().trim().min(2).max(120),
    email: z.string().trim().email().max(254),
    phone: z.string().trim().min(8).max(20),
    planCode: z.enum(['ESSENTIEL', 'PRO', 'RESEAU']),
    acceptTerms: z.literal(true, { message: 'Acceptation des conditions requise' }),
    /** Champ piège invisible : rempli par un robot, la demande est ignorée. */
    website: z.string().max(200).optional(),
  })
  .strict();

export const signupRequestSchema = z.object({
  id: z.string(),
  agencyName: z.string(),
  agentCode: z.string().nullable(),
  taxId: z.string(),
  city: z.string(),
  insurers: z.string(),
  adminName: z.string(),
  email: z.string(),
  phone: z.string(),
  planCode: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  decisionNote: z.string().nullable(),
  createdTenantId: z.string().nullable(),
  createdAt: z.string(),
});
